import { useEffect, useRef, useCallback, useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * useRealtimeQuests
 * Subscribes to:
 *  - player_quests UPDATE  → live progress bar updates
 *  - player_quests INSERT  → new quest assigned
 *  - quest_completions INSERT → quest completed animation + toast
 *  - player_achievements INSERT/UPDATE → achievement unlock popup
 *  - season_player_progress UPDATE → tier-up animation, XP bar
 */
export function useRealtimeQuests({
  userId,
  onQuestProgress,   // (playerQuest) => void
  onQuestComplete,   // (playerQuest) => void
  onAchievementUnlock, // (playerAchievement) => void
  onAchievementProgress, // (playerAchievement) => void
  onSeasonProgress,  // (seasonProgress) => void
  onTierUp,          // (seasonProgress, prevTier) => void
  onWeeklyBonus,     // (bonus) => void
  onLeaderboardUpdate, // (entry) => void
  enabled = true,
}) {
  const channelsRef = useRef([]);
  const mountedRef = useRef(true);
  const prevTierRef = useRef(null);

  const cleanup = useCallback(() => {
    channelsRef?.current?.forEach(ch => {
      try { supabase?.removeChannel(ch); } catch (e) {}
    });
    channelsRef.current = [];
  }, []);

  const subscribe = useCallback(() => {
    if (!userId || !enabled || !mountedRef?.current) return;
    cleanup();

    // 1. player_quests UPDATE — progress bar updates
    const questProgressChannel = supabase
      ?.channel(`rt-quest-progress-${userId}`)
      ?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'player_quests',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        const updated = payload?.new;
        if (!updated) return;
        // Check if just completed
        const wasCompleted = !payload?.old?.completed_at && updated?.completed_at;
        if (wasCompleted) {
          onQuestComplete?.(updated);
        } else {
          onQuestProgress?.(updated);
        }
      })
      ?.subscribe();

    // 2. quest_completions INSERT — completion event
    const questCompleteChannel = supabase
      ?.channel(`rt-quest-complete-${userId}`)
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'quest_completions',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        onQuestComplete?.(payload?.new);
      })
      ?.subscribe();

    // 3. player_achievements INSERT — new achievement unlocked
    const achUnlockChannel = supabase
      ?.channel(`rt-ach-unlock-${userId}`)
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'player_achievements',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        onAchievementUnlock?.(payload?.new);
      })
      ?.subscribe();

    // 4. player_achievements UPDATE — tier progress
    const achProgressChannel = supabase
      ?.channel(`rt-ach-progress-${userId}`)
      ?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'player_achievements',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        onAchievementProgress?.(payload?.new);
      })
      ?.subscribe();

    // 5. season_player_progress UPDATE — XP bar + tier up
    const seasonChannel = supabase
      ?.channel(`rt-season-progress-${userId}`)
      ?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'season_player_progress',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        const updated = payload?.new;
        const prev = payload?.old;
        onSeasonProgress?.(updated);
        // Detect tier up
        if (prev?.current_tier !== updated?.current_tier && prevTierRef?.current !== updated?.current_tier) {
          prevTierRef.current = updated?.current_tier;
          onTierUp?.(updated, prev?.current_tier);
        }
      })
      ?.subscribe();

    // 6. season_player_progress INSERT — first time joining season
    const seasonInsertChannel = supabase
      ?.channel(`rt-season-insert-${userId}`)
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'season_player_progress',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        onSeasonProgress?.(payload?.new);
      })
      ?.subscribe();

    // 7. notifications INSERT — weekly bonus available
    const weeklyBonusChannel = supabase
      ?.channel(`rt-weekly-bonus-${userId}`)
      ?.on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        const notif = payload?.new;
        if (notif?.notification_type === 'weekly_bonus' || notif?.title?.toLowerCase()?.includes('weekly')) {
          onWeeklyBonus?.(notif);
        }
      })
      ?.subscribe();

    // 8. season_leaderboards UPDATE — rank changes
    const leaderboardChannel = supabase
      ?.channel(`rt-leaderboard-${userId}`)
      ?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'season_leaderboards',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        if (!mountedRef?.current) return;
        onLeaderboardUpdate?.(payload?.new, payload?.old);
      })
      ?.subscribe();

    channelsRef.current = [
      questProgressChannel,
      questCompleteChannel,
      achUnlockChannel,
      achProgressChannel,
      seasonChannel,
      seasonInsertChannel,
      weeklyBonusChannel,
      leaderboardChannel,
    ]?.filter(Boolean);
  }, [userId, enabled, onQuestProgress, onQuestComplete, onAchievementUnlock, onAchievementProgress, onSeasonProgress, onTierUp, onWeeklyBonus, onLeaderboardUpdate, cleanup]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled && userId) subscribe();
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [subscribe, enabled, userId]);
}

/**
 * useQuestPageRealtime
 * Higher-level hook that manages all real-time state for the quests page.
 */
export function useQuestPageRealtime({ userId, onToast }) {
  const [realtimePlayerQuests, setRealtimePlayerQuests] = useState({});
  const [realtimePlayerAchievements, setRealtimePlayerAchievements] = useState({});
  const [realtimeSeasonProgress, setRealtimeSeasonProgress] = useState(null);
  const [completedQuestId, setCompletedQuestId] = useState(null);
  const [unlockedAchievement, setUnlockedAchievement] = useState(null);
  const [tierUpData, setTierUpData] = useState(null);
  const [rankChange, setRankChange] = useState(null);

  const handleQuestProgress = useCallback((playerQuest) => {
    setRealtimePlayerQuests(prev => ({
      ...prev,
      [playerQuest?.quest_id]: playerQuest,
    }));
  }, []);

  const handleQuestComplete = useCallback((playerQuest) => {
    setRealtimePlayerQuests(prev => ({
      ...prev,
      [playerQuest?.quest_id]: playerQuest,
    }));
    setCompletedQuestId(playerQuest?.quest_id || playerQuest?.id);
    onToast?.('quest_complete', playerQuest);
    // Clear animation after 4s
    setTimeout(() => setCompletedQuestId(null), 4000);
  }, [onToast]);

  const handleAchievementUnlock = useCallback((playerAch) => {
    setRealtimePlayerAchievements(prev => ({
      ...prev,
      [playerAch?.achievement_id]: playerAch,
    }));
    setUnlockedAchievement(playerAch);
    onToast?.('achievement_unlock', playerAch);
    setTimeout(() => setUnlockedAchievement(null), 5000);
  }, [onToast]);

  const handleAchievementProgress = useCallback((playerAch) => {
    setRealtimePlayerAchievements(prev => ({
      ...prev,
      [playerAch?.achievement_id]: playerAch,
    }));
  }, []);

  const handleSeasonProgress = useCallback((progress) => {
    setRealtimeSeasonProgress(progress);
  }, []);

  const handleTierUp = useCallback((progress, prevTier) => {
    setTierUpData({ progress, prevTier });
    onToast?.('tier_up', { progress, prevTier });
    setTimeout(() => setTierUpData(null), 5000);
  }, [onToast]);

  const handleWeeklyBonus = useCallback((notif) => {
    onToast?.('weekly_bonus', notif);
  }, [onToast]);

  const handleLeaderboardUpdate = useCallback((entry, prevEntry) => {
    const rankDiff = (prevEntry?.rank || 0) - (entry?.rank || 0);
    setRankChange({ rank: entry?.rank, diff: rankDiff });
    onToast?.('rank_change', { entry, prevEntry, diff: rankDiff });
    setTimeout(() => setRankChange(null), 4000);
  }, [onToast]);

  useRealtimeQuests({
    userId,
    onQuestProgress: handleQuestProgress,
    onQuestComplete: handleQuestComplete,
    onAchievementUnlock: handleAchievementUnlock,
    onAchievementProgress: handleAchievementProgress,
    onSeasonProgress: handleSeasonProgress,
    onTierUp: handleTierUp,
    onWeeklyBonus: handleWeeklyBonus,
    onLeaderboardUpdate: handleLeaderboardUpdate,
    enabled: !!userId,
  });

  return {
    realtimePlayerQuests,
    realtimePlayerAchievements,
    realtimeSeasonProgress,
    completedQuestId,
    unlockedAchievement,
    tierUpData,
    rankChange,
  };
}
